import { useCallback, useEffect, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { api, errMsg } from "../../lib/api";
import { dateTime, humanize } from "../../lib/format";
import { DataTable, PageHeader, StatusPill, TestBadge } from "../../components/admin/ui";

const TOGGLES = [["registration_required", "Zoom registration required"], ["embedded_enabled", "Embedded join enabled"],
  ["backup_link_enabled", "Backup link enabled"], ["live_access_enabled", "Live (non-test) access enabled"]];

const Yes = ({ ok, label, testid }) => (
  <li className="flex items-center gap-2" data-testid={testid}>{ok ? <CheckCircle2 size={17} className="text-green-700" aria-hidden="true" /> : <XCircle size={17} className="text-burgundy" aria-hidden="true" />}
    {label}: <b>{ok ? "Yes" : "No"}</b></li>
);

function StatusPanel({ cfg, onTest, canEdit, testing }) {
  const s = cfg.status, t = cfg.last_signature_test;
  return (
    <div className="admin-card" data-testid="zoom-connection-status">
      <h2 className="text-lg text-navy">Connection status</h2>
      <ul className="mt-3 space-y-2 text-sm">
        <Yes ok={s.sdk_credentials_present} label="Meeting SDK credentials present" testid="status-sdk" />
        <Yes ok={s.s2s_credentials_present} label="Server-to-Server OAuth (registrants)" testid="status-s2s" />
        <Yes ok={s.email_provider_configured} label={`Email provider${s.email_provider ? ` (${s.email_provider})` : ""}`} testid="status-email" />
        <Yes ok={cfg.meeting_id_set} label="Meeting/webinar ID set" testid="status-meeting-id" />
      </ul>
      <p className="mt-3 text-sm" data-testid="last-signature-test">Last signature test: {t ? <><StatusPill value={t.ok ? "completed" : "failed"} /> {t.message} <span className="text-slate-500">({dateTime(t.at)})</span></> : "never"}</p>
      {canEdit && <button type="button" className="btn-navy btn-sm mt-4" onClick={onTest} disabled={testing} data-testid="test-signature-btn">{testing ? "Testing…" : "Test signature generation"}</button>}
      {!s.email_provider_configured && <p className="mt-3 text-xs text-[#4A5568]">Live delegate access stays disabled until an email provider is configured (codes are only logged for test registrations in development).</p>}
    </div>
  );
}

export default function VirtualAdmin() {
  const { canEdit } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    api.get("/admin/virtual/config").then((r) => { setCfg(r.data); setForm({ ...r.data, meeting_id: "", passcode: "", clear_passcode: false }); });
    api.get("/admin/virtual-attendance").then((r) => setRows(r.data.attendees));
  }, []);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const save = async (e) => {
    e.preventDefault(); setBusy(true);
    const keys = ["session_type", "meeting_id", "passcode", "clear_passcode", "access_start", "access_end", ...TOGGLES.map(([k]) => k)];
    try { await api.put("/admin/virtual/config", Object.fromEntries(keys.map((k) => [k, form[k]]))); toast.success("Virtual settings saved"); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const testSig = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/virtual/test-signature"); data.ok ? toast.success(data.message) : toast.error(data.message); load(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  const approve = async (r, approved) => {
    try { await api.post(`/admin/virtual/approve/${r.id}`, { approved }); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  if (!cfg || !form) return <p aria-busy="true">Loading…</p>;

  const cols = [
    { key: "name", label: "Delegate", render: (r) => <span className="font-semibold text-navy">{r.first_name} {r.last_name} <TestBadge show={r.is_test} /></span> },
    { key: "reference", label: "Reference", render: (r) => <span className="font-mono text-xs">{r.reference}</span> },
    { key: "ps", label: "Payment", render: (r) => <StatusPill value={r.payment_status} /> },
    { key: "access", label: "Access", render: (r) => <span title={r.access_message}><StatusPill value={r.access_eligible ? "approved" : r.access_reason} testid={`access-status-${r.reference}`} /></span> },
    { key: "comm", label: "Last communication", render: (r) => r.last_communication ? <span className="text-xs">{humanize(r.last_communication.template)} · <StatusPill value={r.last_communication.status} /></span> : "—" },
    { key: "act", label: "", render: (r) => canEdit && r.payment_status === "not_required" && r.registration_status === "confirmed" ? (
      <button type="button" className="text-xs font-semibold text-burgundy" onClick={() => approve(r, !r.admin_approved)} data-testid={`approve-virtual-${r.reference}`}>
        {r.admin_approved ? "Revoke approval" : "Approve complimentary"}</button>) : null },
  ];
  const disabled = !canEdit;
  return (
    <div data-testid="admin-virtual">
      <PageHeader title="Virtual Attendance" subtitle="Zoom access for verified, eligible virtual delegates. SDK credentials: Admin › Integrations › Zoom (Super Admin) or server env. Meeting ID / passcode here are the same settings shown there." />
      <div className="grid gap-6 xl:grid-cols-3">
        <form onSubmit={save} className="admin-card grid gap-4 md:grid-cols-2 xl:col-span-2" data-testid="virtual-config-form">
          <label className="field-label" htmlFor="v-type">Session type
            <select id="v-type" className="field-input mt-1 text-sm font-normal" value={form.session_type} onChange={set("session_type")} disabled={disabled} data-testid="virtual-session-type-select">
              <option value="meeting">Meeting</option><option value="webinar">Webinar</option></select></label>
          <label className="field-label" htmlFor="v-mid">Meeting / webinar ID
            <input id="v-mid" className="field-input mt-1 text-sm font-normal" inputMode="numeric" placeholder={cfg.meeting_id_set ? `${cfg.meeting_id_masked} (leave blank to keep)` : "Not set"}
              value={form.meeting_id} onChange={set("meeting_id")} disabled={disabled} autoComplete="off" data-testid="virtual-meeting-id-input" /></label>
          <label className="field-label" htmlFor="v-pass">Passcode
            <input id="v-pass" type="password" className="field-input mt-1 text-sm font-normal" placeholder={cfg.passcode_set ? "•••••• (set; leave blank to keep)" : "Not set"}
              value={form.passcode} onChange={set("passcode")} disabled={disabled} autoComplete="new-password" data-testid="virtual-passcode-input" /></label>
          <div className="flex items-end">{cfg.passcode_set && <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.clear_passcode} onChange={(e) => set("clear_passcode")(e.target.checked)} disabled={disabled} /> Clear passcode</label>}</div>
          <label className="field-label" htmlFor="v-start">Access window start ({"Nassau time"})
            <input id="v-start" type="datetime-local" className="field-input mt-1 text-sm font-normal" value={form.access_start} onChange={set("access_start")} disabled={disabled} data-testid="virtual-access-start-input" /></label>
          <label className="field-label" htmlFor="v-end">Access window end
            <input id="v-end" type="datetime-local" className="field-input mt-1 text-sm font-normal" value={form.access_end} onChange={set("access_end")} disabled={disabled} data-testid="virtual-access-end-input" /></label>
          {TOGGLES.map(([k, l]) => (
            <label key={k} className="flex items-center gap-3 text-sm" htmlFor={`v-${k}`}>
              <Switch id={`v-${k}`} checked={!!form[k]} onCheckedChange={set(k)} disabled={disabled} data-testid={`virtual-toggle-${k}`} />{l}</label>
          ))}
          <div className="md:col-span-2">
            {canEdit ? <button type="submit" className="btn-primary btn-sm" disabled={busy} data-testid="virtual-config-save-btn">Save settings</button>
              : <p className="text-sm text-slate-600" data-testid="virtual-readonly-note">Read-only: Viewers cannot change virtual settings.</p>}
          </div>
        </form>
        <StatusPanel cfg={cfg} onTest={testSig} canEdit={canEdit} testing={busy} />
      </div>
      <h2 className="mb-3 mt-8 text-xl text-navy">Virtual delegates</h2>
      <DataTable columns={cols} rows={rows} testid="virtual-delegates-table" empty="No virtual registrations." />
    </div>
  );
}
